import { Component, ViewChild } from '@angular/core';
import { EntityQuery } from '@cime/breeze-client';
import { ConveyancePermissions, VesselNotificationPermissions, VesselPermitPermissions, VesselShiftPermissions, VesselShortNotificationPermissions } from '@common/classes/permissions';
import { User } from '@common/models/User';
import { BreezeViewService } from '@common/services/breeze-view.service';
import { ConveyanceGridComponent } from 'app/conveyance/components/conveyance-grid/conveyance-grid.component';
import { VesselNotificationGridComponent } from 'app/vessel-notification/components/vessel-notification-grid/vessel-notification-grid.component';
import { VesselPermitGridComponent } from 'app/vessel-permit/components/vessel-permit-grid/vessel-permit-grid.component';
import { VesselShiftGridComponent } from 'app/vessel-shift/components/vessel-shift-grid/vessel-shift-grid.component';
import { VesselShortNotificationGridComponent } from 'app/vessel-short-notification/components/vessel-short-notification-grid/vessel-short-notification-grid.component';
import { debounceTime, Subject } from 'rxjs';

@Component({
    selector: 'app-task-manager',
    templateUrl: './task-manager.component.html',
    styleUrls: ['./task-manager.component.scss'],
    providers: [BreezeViewService]
})
export class TaskManagerComponent {
    userSubscription;
    vesselNotificationQuery;
    vesselPermitQuery;
    conveyanceQuery;
    vesselShiftQuery;
    vesselShortNotificationQuery;
    activeTab = 0;
    user: User;

    vesselNotificationFilterChanged = new Subject<any>();
    vesselNotificationFilter = {
        portOfCallId: null,
        notificationTypeId: null,
        imoNumber: null,
        vesselId: null
    };

    vesselShiftFilterChanged = new Subject<any>();
    vesselShiftFilter = {
        vesselId: null,
        imoNumber: null,
        berthShiftingFromId: null,
        berthShiftingToId: null
    };

    shortNotificationFilterChanged = new Subject<any>();
    shortNotificationFilter = {
        portOfCallId: null,
        notificationTypeId: null,
        imoNumber: null,
        vesselId: null
    };

    @ViewChild(VesselNotificationGridComponent)
    public vesselNotificationGrid: VesselNotificationGridComponent;

    @ViewChild(VesselPermitGridComponent)
    public vesselPermitGrid: VesselPermitGridComponent;

    @ViewChild(ConveyanceGridComponent)
    public conveyanceGrid: ConveyanceGridComponent;

    @ViewChild(VesselShiftGridComponent)
    public vesselShiftGrid: VesselShiftGridComponent;

    @ViewChild(VesselShortNotificationGridComponent)
    public vesselShortNotificationGrid: VesselShortNotificationGridComponent;

    constructor(breezeViewService: BreezeViewService) {
        const user = breezeViewService.userService.currentUserSubject.getValue();
        this.user = user;

        if (!user) return;

        if (user.hasPermission(VesselNotificationPermissions.View.own, VesselNotificationPermissions.View.all)) {
            this.fetchVesselNotifications();
        }

        if (user.hasPermission(VesselShiftPermissions.View.own, VesselShiftPermissions.View.all)) {
            this.fetchVesselShifts();
        }

        if (user.hasPermission(VesselShortNotificationPermissions.View.own, VesselShortNotificationPermissions.View.all)) {
            this.fetchShortNotifications();
        }

        this.vesselNotificationFilterChanged
            .pipe(debounceTime(500))
            .subscribe(() => this.fetchVesselNotifications());
        this.vesselShiftFilterChanged
            .pipe(debounceTime(500))
            .subscribe(() => this.fetchVesselShifts());
        this.shortNotificationFilterChanged
            .pipe(debounceTime(500))
            .subscribe(() => this.fetchShortNotifications());
    }

    onVesselNotificationFilterChange(event) {
        this.vesselNotificationFilterChanged.next(this.vesselNotificationFilter);
    }

    onVesselShiftFilterChange(event) {
        this.vesselShiftFilterChanged.next(this.vesselShiftFilter);
    }

    onShortNotificationFilterChange(event) {
        this.shortNotificationFilterChanged.next(this.shortNotificationFilter);
    }

    fetchVesselNotifications() {
        const statusParameters = this.user?.isAgent() ? ['W', 'D', 'RM'] : ['W'];
        this.vesselNotificationQuery = EntityQuery.from('VesselNotifications')
        .withParameters({
            $method: 'POST',
            $data: { statusIds: statusParameters, filterByOrganization: true, isShort: false, ...this.vesselNotificationFilter }
        });
    }

    fetchVesselShifts() {
        const statusParameters = this.user?.isAgent() ? ['W', 'D'] : ['W'];
        this.vesselShiftQuery = EntityQuery.from('VesselShifts')
            .withParameters({
                $method: 'POST',
                $data: { statusIds: statusParameters, filterByOrganization: true, ...this.vesselShiftFilter }
            });
    }

    fetchShortNotifications() {
        const statusParameters = this.user?.isAgent() ? ['W', 'D'] : ['W'];
        this.vesselShortNotificationQuery = EntityQuery.from('VesselNotifications')
        .withParameters({
            $method: 'POST',
            $data: { statusIds: statusParameters, filterByOrganization: !this.user?.isPort(), isShort: true, ...this.shortNotificationFilter }
        });
    }

    activateTab(tab) {
        this.activeTab = this.activeTab === tab ?  0 : tab;
    }

    hasAnyPermissions(): boolean {
        return this.user?.hasPermission(VesselNotificationPermissions, VesselPermitPermissions, ConveyancePermissions, VesselShiftPermissions, VesselShortNotificationPermissions);
    }

    isTabVisible(tabs: any[]) {
        let res = false;
        tabs.forEach(tab => {
            switch(tab) {
                case 1:
                    res ||= !!this.vesselNotificationQuery;
                    break;
                case 2:
                    res ||= !!this.vesselPermitQuery;
                    break;
                case 3:
                    res ||= !!this.vesselShortNotificationQuery;
                    break;
                case 4:
                    res ||= !!this.conveyanceQuery;
                    break;
                case 5:
                    res ||= !!this.vesselShiftQuery;
                    break;
            }
        });

        return res;
    }
}
